import { createFileRoute } from '@tanstack/react-router';
import PageTitle from 'src/components/creator/PageTitle';
import FAQAccordion, { FAQItem } from 'src/components/FAQAccordion';

const CreatorFAQPage = () => {
  return (
    <>
      <PageTitle title="Frequently Asked Questions" />
      <div className="bg-surface-alt rounded-xl shadow-3 px-2 mt-4 min-h-0 overflow-y-auto">
        <FAQAccordion list={CREATOR_FAQ_LIST} />
      </div>
    </>
  );
};

const CREATOR_FAQ_LIST: FAQItem[] = [
  {
    question: 'What is Creator Studio?',
    answer: [
      'Creator Studio is your workspace for creating and managing course materials. You can submit course pack requests and track the status of your orders - all in one place.',
    ],
  },
  {
    question: 'How do I create a new course pack request?',
    answer: [
      'Go to Order Requests and click Create Order. Fill in the required course and instructor details, then follow the steps to submit your request.',
    ],
  },
  {
    question: 'What information do I need to create an order?',
    answer: [
      "You'll typically need:",
      `
        <ul class="list-disc list-inside ml-1 my-1">
          <li>Course name and number</li>
          <li>Term and year</li>
          <li>Quantity</li>
          <li>Bookstore and institution</li>
          <li>Instructor or contact details</li>
          <li>Course material details</li>
        </ul>
      `,
      'Providing complete and accurate information helps us process your request faster.',
    ],
  },
  {
    question: 'How can I track my order?',
    answer: [
      'You can track all your requests in the Order Requests dashboard. Each order shows its current status as it progresses.',
    ],
  },
  {
    question: 'What does the order status mean?',
    answer: [
      'Each order has a status that shows its current progress:',
      `
        <ul class="list-disc list-inside ml-1 my-1">
          <li><strong>New</strong> - Your request has been submitted and is waiting to be reviewed</li>
          <li><strong>In Progress</strong> - Your request is being processed by our team</li>
          <li><strong>Completed</strong> - Your course materials have been finalized</li>
          <li><strong>No Status</strong> - The request has not yet been fully processed</li>
        </ul>
      `,
      "Statuses may update as your order moves through different stages. If you're unsure about the status of your order, feel free to contact support.",
    ],
  },
  {
    question: 'Can I edit or update an order?',
    answer: [
      'Yes. Open the Actions menu next to your order and select <strong>Edit</strong>, <strong>Reorder with Changes</strong>, or <strong>Exact Repeat</strong>, depending on what you need and the status of the order.',
    ],
  },
  {
    question: 'What actions can I perform on an order?',
    answer: [
      'You can manage your orders using the Actions menu next to each request. Available options may vary depending on the order status.',
      'Common actions include:',
      `
        <ul class="list-disc list-inside ml-1 my-1">
          <li><strong>Edit</strong> - Update details of your existing order</li>
          <li><strong>Reorder with Changes</strong> - Create a new order based on an existing one, with modifications</li>
          <li><strong>Exact Repeat</strong> - Duplicate an order for a new term without making changes</li>
          <li><strong>Purchase Order</strong> - Proceed with purchasing or processing the order</li>
          <li><strong>Preview</strong> - View the details of your order before taking further action</li>
        </ul>
      `,
      'Not all actions are available for every order. The options you see depend on the current status of the request.',
    ],
  },
  {
    question: 'How does the "Filter by Store" option work?',
    answer: [
      'The "Filter by Store" option allows you to view orders associated with a specific bookstore.',
      'Simply select a store from the dropdown to filter your order list. This helps you quickly find and manage requests related to a particular bookstore.',
      'If no store is selected, all orders will be displayed.',
    ],
  },
  {
    question: 'Why should I use the store filter?',
    answer: [
      'Use the store filter when:',
      `
        <ul class="list-disc list-inside ml-1 my-1">
          <li>You work with multiple bookstores</li>
          <li>You want to focus on orders for a specific institution</li>
          <li>You need to quickly locate a particular request</li>
        </ul>
      `,
      'Filtering makes it easier to navigate and manage large lists of orders.',
    ],
  },
  {
    question: 'What is the Out-of-Print Library?',
    answer: [
      'The Out-of-Print Library allows you to search for hard-to-find or unavailable titles. You can search by title, author, or ISBN and request materials directly from the platform.',
    ],
  },
  {
    question:
      "What should I do if I can't find a title in the Out-of-Print Library?",
    answer: [
      "If you don't find what you're looking for, you can submit a request and our team will help source the material or explore available options.",
    ],
  },
  {
    question: 'What is AI Assisted Search?',
    answer: [
      'AI Assisted Search helps you quickly find relevant course materials by describing what you need. It can suggest content from multiple sources to support your course.',
    ],
  },
  {
    question: 'How do I use AI Assisted Search?',
    answer: [
      "Simply type what you're looking for in the input field (e.g., topic, book, or material), and the system will suggest relevant options.",
    ],
  },
  {
    question: 'How do I contact support?',
    answer: [
      'You can use the Contact Us page inside Creator Studio to send a message directly to our support team.',
    ],
  },
  {
    question: 'What should I include when contacting support?',
    answer: [
      'To help us assist you faster, include:',
      `
        <ul class="list-disc list-inside ml-1 my-1">
          <li>Course name or order reference</li>
          <li>A clear description of your issue</li>
          <li>Any relevant details or screenshots</li>
        </ul>
      `,
    ],
  },
  {
    question: 'Can I invite other faculty members?',
    answer: [
      'Yes. Use the Invite Faculty Member option to add collaborators who can help manage course materials.',
    ],
  },
];

export const Route = createFileRoute('/_authenticated/_creator/creator-faq')({
  component: CreatorFAQPage,
});
